import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  BooleanField,
  FunctionField,
  Show,
  TabbedShowLayout,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  BooleanInput,
  required,
  email,
  TopToolbar,
  ExportButton,
  FilterButton,
  useRecordContext,
  ArrayField,
  EditButton,
} from "react-admin";
import { Typography, Alert, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { PersonChip, TechInfo } from "../../components/ui";
import { EnumChip } from "../../components/EnumChip";
import { JsonField } from "../../components/JsonField";
import {
  USER_TYPE,
  ACCOUNT_STATUS,
  GENDER,
  choicesFromEnum,
  isProType,
} from "../../enums";
import { FrDateField } from "../../components/FrDateField";

// Spécialités d'un pro : ProfessionalDTO.professionalCard.specialities[].speciality.name
export const specialitiesOf = (r) =>
  (r?.professionalCard?.specialities || [])
    .map((sp) => sp?.speciality?.name)
    .filter(Boolean)
    .join(", ");

// Filtres réellement supportés par GET /users (les autres — recherche
// texte, genre, statut, dates — étaient ignorés par le backend).
const userFilters = [
  <SelectInput key="type" source="type" label="Type" choices={choicesFromEnum(USER_TYPE)} alwaysOn />,
  <BooleanInput
    key="hideTestUsers"
    source="hideTestUsers"
    label="Masquer les comptes de test"
    defaultValue={true}
  />,
  <BooleanInput key="showDeleted" source="showDeleted" label="Afficher les comptes supprimés" />,
];

// Pas de création générique (aucun POST /users) : passer par
// « Inviter un pro / patient ».
const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

export const UserList = () => (
  <List
    filters={userFilters}
    filterDefaultValues={{ hideTestUsers: true }}
    actions={<ListActions />}
    perPage={25}
  >
    <Datagrid rowClick={(id, res, r) => fullPageOf(r) || "show"}>
      <FunctionField
        label="Utilisateur"
        sortBy="lastName"
        render={(r) => (
          <PersonChip firstName={r.firstName} lastName={r.lastName} email={r.email} photo={r?.photo?.url} resource={isProType(r?.type) ? "professionals" : "patients"} />
        )}
      />
      <FunctionField label="Téléphone" render={(r) => r?.phoneNumber || "—"} />
      <EnumChip source="type" label="Type" enumObj={USER_TYPE} />
      <EnumChip source="accountStatus" enumObj={ACCOUNT_STATUS} label="Statut" />
      <FrDateField source="creationDate" label="Inscrit le" />
    </Datagrid>
  </List>
);

const FullName = () => {
  const r = useRecordContext();
  if (!r) return null;
  return (
    <Typography variant="h6">
      {[r.firstName, r.lastName].filter(Boolean).join(" ")}
    </Typography>
  );
};

// Fiche complète d'un compte typé (patient / pro) ; null si type inconnu.
const fullPageOf = (r) =>
  isProType(r?.type) ? `/professionals/${encodeURIComponent(r.id)}/show`
    : r?.type === "PATIENT" ? `/patients/${encodeURIComponent(r.id)}/show` : null;

const TypeNotice = () => {
  const r = useRecordContext();
  if (!r) return null;
  const to = fullPageOf(r);
  return to ? (
    <Alert severity="info" sx={{ mb: 2 }} action={<Button component={Link} to={to} size="small">Ouvrir la fiche complète</Button>}>
      Ce compte a une fiche détaillée ({isProType(r.type) ? "professionnel" : "patient"}) : rendez-vous, paiements, documents…
    </Alert>
  ) : (
    <Alert severity="warning" sx={{ mb: 2 }}>
      Compte sans type (ni patient ni professionnel) : lecture seule ici. Pour le modifier, retrouvez-le dans
      « Patients », « Professionnels » ou « Comptes incomplets ».
    </Alert>
  );
};

const UserShowActions = () => {
  const r = useRecordContext();
  return <TopToolbar>{r?.type ? <EditButton /> : null}</TopToolbar>;
};

export const UserShow = () => (
  <Show actions={<UserShowActions />}>
    <TypeNotice />
    <TabbedShowLayout>
      <TabbedShowLayout.Tab label="Profil">
        <FullName />
        <EmailField source="email" />
        <TextField source="phoneNumber" label="Téléphone" />
        <EnumChip source="type" enumObj={USER_TYPE} />
        <EnumChip source="accountStatus" enumObj={ACCOUNT_STATUS} label="Statut" />
        <EnumChip source="gender" enumObj={GENDER} />
        <FrDateField source="birthdate" label="Date de naissance" />
        <TextField source="socialType" label="Connexion sociale" />
        <FrDateField source="creationDate" label="Inscrit le" showTime />
        <FrDateField source="updateDate" label="Mis à jour" showTime />
        <BooleanField source="active" label="Actif" />
        <BooleanField source="emailVerified" label="Email vérifié" />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Adresse">
        <TextField source="address.street" label="Rue" />
        <TextField source="address.city" label="Ville" />
        <TextField source="address.postalCode" label="Code postal" />
        <TextField source="address.country" label="Pays" />
        <TextField source="address.latitude" label="Latitude" />
        <TextField source="address.longitude" label="Longitude" />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Pro">
        <TextField source="job" label="Métier" />
        <FunctionField label="Spécialités" render={(r) => specialitiesOf(r) || "—"} />
        <FunctionField label="Validé" render={(r) => (r?.accountStatus === "ACTIVE" ? "Oui" : "Non")} />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Préférences">
        <JsonField source="settings" label="Paramètres" />
        <JsonField source="preferences" label="Préférences" />
        <JsonField source="traits" label="Traits" />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Proches">
        <ArrayField source="proches">
          <Datagrid bulkActionButtons={false}>
            <TextField source="firstName" label="Prénom" />
            <TextField source="lastName" label="Nom" />
            <TextField source="gender" label="Genre" />
            <TextField source="phone" label="Téléphone" />
            <EmailField source="email" label="Email" />
          </Datagrid>
        </ArrayField>
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Informations techniques">
        <TechInfo defaultExpanded />
      </TabbedShowLayout.Tab>
    </TabbedShowLayout>
  </Show>
);

// PUT /users/pro/{id} ou /users/patient/{id} selon le type (cf.
// resourceMap.users.updatePath) ; le corps est l'enregistrement complet
// (champs du DTO backend). Photo : POST /users/{id}/photo (hors formulaire).
const UserForm = () => (
  <SimpleForm>
    <TextInput source="firstName" label="Prénom" validate={required()} />
    <TextInput source="lastName" label="Nom" validate={required()} />
    <TextInput source="email" label="Email" type="email" validate={[required(), email()]} />
    <TextInput source="phoneNumber" label="Téléphone" />
    <SelectInput source="gender" label="Genre" choices={choicesFromEnum(GENDER)} />
    <SelectInput source="accountStatus" label="Statut du compte" choices={choicesFromEnum(ACCOUNT_STATUS)} />
    <TextInput source="address.street" label="Rue" />
    <TextInput source="address.postalCode" label="Code postal" />
    <TextInput source="address.city" label="Ville" />
    <TextInput source="address.country" label="Pays" />
  </SimpleForm>
);

export const UserEdit = () => (
  <Edit mutationMode="pessimistic">
    <UserForm />
  </Edit>
);

export default {
  list: UserList,
  show: UserShow,
  edit: UserEdit,
};
